import { ButtonHTMLAttributes, FunctionComponent, ReactNode } from 'react';

type IconButtonColor = 'inherit' | 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
type IconButtonSize = 'small' | 'medium' | 'large';

type IconButtonUIProps = {
  color?: IconButtonColor,
  size?: IconButtonSize,
  children?: ReactNode,
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'color'>;

const SIZE: Record<IconButtonSize, string> = {
  small: 'sm',
  medium: 'md',
  large: 'lg',
};

/**
 * Icon button.
 *
 * Appearance now comes from the design system's `.ds-icon-btn` (a rounded
 * square sized off `--ds-control-h`) instead of the old MUI circle. The prop
 * API is unchanged.
 *
 * Two details worth keeping in mind:
 *  - `font-size` stays an inline style. Icons default to `1em`, so the button's
 *    font-size is what actually sizes the glyph; leaving it to inherit would
 *    shrink every icon in the app to body-text size. The consequence: a call-site
 *    `text-[10px]` class CANNOT take effect here - inline styles outrank every
 *    stylesheet rule, including the `#root`-scoped Tailwind utilities. Nor can
 *    `px-*`/`py-*`, since `.ds-icon-btn` is a fixed `--ds-control-h` square. If you
 *    need a sizable text label, this is the wrong component: use `Chip` or `Button`.
 *  - `color="default"` deliberately sets no tone, so the icon takes
 *    `.ds-icon-btn`'s muted colour and its hover treatment.
 */
const ICON_SIZE: Record<IconButtonSize, number> = {
  small: 18,
  medium: 20,
  large: 24,
};

const IconButtonUI: FunctionComponent<IconButtonUIProps> = ({
  color = 'default',
  size = 'medium',
  disabled,
  className = '',
  style,
  type,
  children,
  ...rest
}) => (
  <button
    type={type ?? 'button'}
    className={`ds-icon-btn ${className}`}
    data-ds-size={SIZE[size]}
    data-ds-tone={color === 'default' ? undefined : color}
    disabled={disabled}
    // Call-site `style` still wins over the base, exactly as before.
    style={{ fontSize: ICON_SIZE[size], ...style }}
    {...rest}
  >
    {children}
  </button>
);

export default IconButtonUI;
